import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["editable"]
    static values = {
        url: String
    }

    connect() {
        this.saveTimeout = null
    }

    // ==========================================
    // INLINE TEXT EDITING
    // ==========================================

    updateText(event) {
        // Debounce save
        if (this.saveTimeout) clearTimeout(this.saveTimeout)

        this.saveTimeout = setTimeout(() => {
            this.saveText(event)
        }, 1000)
    }

    saveText(event) {
        if (this.saveTimeout) clearTimeout(this.saveTimeout)

        const element = event.target
        const field = element.dataset.pageEditorFieldParam
        let value = element.innerText // Start with innerText for safety

        // For rich text blocks (todo), might need innerHTML but sanitized
        // For now, MVP supports plain text with basic formatting if contenteditable allows it.
        // However, innerText usually strips tags. innerHTML keeps them.
        // Let's use innerHTML but rely on server sanitization.
        value = element.innerHTML

        this.sendUpdate(field, value)
    }

    // Prevent bubbling to the global "Edit Block" listener when clicking text
    stopProp(event) {
        event.stopPropagation()
    }

    // ==========================================
    // SPACING DRAG HANDLER
    // ==========================================

    startResize(event) {
        event.preventDefault()
        event.stopPropagation()

        this.direction = event.currentTarget.dataset.direction
        this.startY = event.clientY

        // Get current padding
        const style = window.getComputedStyle(this.element)
        this.startPadding = parseInt(style.getPropertyValue(this.direction === 'top' ? 'padding-top' : 'padding-bottom')) || 0

        // Bind global listeners
        this.boundMouseMove = this.mouseMove.bind(this)
        this.boundMouseUp = this.mouseUp.bind(this)

        document.addEventListener('mousemove', this.boundMouseMove)
        document.addEventListener('mouseup', this.boundMouseUp)

        // Visual feedback
        document.body.style.cursor = 'ns-resize'
        this.element.classList.add('ring-2', 'ring-indigo-400', 'ring-offset-2')
    }

    mouseMove(event) {
        const delta = event.clientY - this.startY
        // If top, dragging down (positive) increases padding? No, wait.
        // Dragging down usually increases top padding.
        // If bottom, dragging down increases bottom padding.

        let newPadding
        if (this.direction === 'top') {
            newPadding = Math.max(0, this.startPadding + delta)
            this.element.style.paddingTop = `${newPadding}px`
        } else {
            newPadding = Math.max(0, this.startPadding + delta)
            this.element.style.paddingBottom = `${newPadding}px`
        }

        // Store for save on mouseup
        this.currentPadding = newPadding
    }

    mouseUp(event) {
        document.removeEventListener('mousemove', this.boundMouseMove)
        document.removeEventListener('mouseup', this.boundMouseUp)
        document.body.style.cursor = ''
        this.element.classList.remove('ring-2', 'ring-indigo-400', 'ring-offset-2')

        // Save to server
        const field = this.direction === 'top' ? 'padding_top' : 'padding_bottom'
        this.sendUpdate(field, this.currentPadding)
    }

    // ==========================================
    // ADD BLOCK HANDLER
    // ==========================================

    addBlock(event) {
        event.preventDefault()
        const button = event.currentTarget

        // Visual feedback
        button.classList.add('loading')

        const url = button.dataset.url
        const blockType = button.dataset.blockType
        const pageId = button.dataset.pageId

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
                'Accept': 'text/vnd.turbo-stream.html'
            },
            body: JSON.stringify({
                page_block: {
                    block_type: blockType,
                    page_id: pageId
                }
            })
        })
            .then(response => {
                if (response.ok) {
                    return response.text()
                }
                throw new Error('Network response was not ok.')
            })
            .then(html => {
                // Turbo Stream handles the DOM update automatically via response
                // But if we requested JSON, we might need manual handling.
                // Controller responds to turbo_stream format.
                // Fetch with Accept: 'text/vnd.turbo-stream.html' executes streams.
                // Turbo.renderStreamMessage(html) might be needed if not using Turbo.visit or native form submission.
                // Actually, simplest is to use requestSubmit or Turbo.visit if possible, but for POST API...
                // Let's use @hotwired/turbo if available, or just fetch and Turbo.renderStreamMessage(html).

                if (window.Turbo) {
                    window.Turbo.renderStreamMessage(html)
                }
            })
            .catch(error => console.error('Error adding block:', error))
            .finally(() => {
                button.classList.remove('loading')
            })
    }


    // ==========================================
    // API COMMUNICATOR
    // ==========================================

    openSettings(event) {
        // Ignore if clicking interactive elements
        if (event.target.isContentEditable ||
            event.target.closest('[contenteditable="true"]') ||
            event.target.closest('button') ||
            event.target.closest('a') ||
            event.target.closest('.drag-handle')) {
            return
        }

        event.preventDefault()
        event.stopPropagation()

        this.openBlockSettings()
    }

    openBlockSettings() {
        const url = this.element.dataset.blockUrl
        if (url && window.loadBlockEditForm) {
            window.loadBlockEditForm(url, this.element)
        } else if (url) {
            // loadBlockEditForm not yet available, try fetching manually
            console.warn("[PageEditor] loadBlockEditForm not ready, fetching directly")
            fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(r => r.ok ? r.text() : Promise.reject('Failed to load'))
                .then(html => {
                    if (window.openSidebar) window.openSidebar(html)
                })
                .catch(e => console.error("[PageEditor] Fallback fetch failed:", e))
        } else {
            // No URL - the sidebar might already be populated (new block via Turbo Stream)
            const sidebar = document.getElementById('edit-sidebar')
            const sidebarContent = document.getElementById('sidebar-content')
            if (sidebar && sidebarContent && sidebarContent.innerHTML.trim().length > 0) {
                sidebar.classList.remove('translate-x-full')
            } else {
                console.warn("[PageEditor] No block URL and no sidebar content available")
            }
        }
    }

    closeSidebar(event) {
        if (event) event.preventDefault()
        if (window.closeSidebar) {
            window.closeSidebar()
        } else {
            const sidebar = document.getElementById('edit-sidebar')
            if (sidebar) sidebar.classList.add('translate-x-full')
        }
    }

    openPageSettings(event) {
        event.preventDefault()
        const url = event.currentTarget.dataset.url
        if (url && window.loadBlockEditForm) window.loadBlockEditForm(url)
    }

    async sendUpdate(field, value) {
        this.showSavingStatus()

        try {
            const response = await fetch(this.urlValue, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({ field, value })
            })

            const data = await response.json()

            if (data.success) {
                this.showSavedStatus()
            } else {
                console.error("Save failed", data)
                this.showErrorStatus()
            }
        } catch (err) {
            console.error("Error saving", err)
            this.showErrorStatus()
        }
    }

    // ==========================================
    // BLOCK ACTIONS
    // ==========================================

    // ==========================================
    // BLOCK ACTIONS
    // ==========================================

    async moveUp(event) {
        console.log("Blocking Action: Move Up");
        event.preventDefault()
        event.stopPropagation()
        const btn = event.currentTarget
        await this.performAction(btn.dataset.url)
    }

    async moveDown(event) {
        console.log("Blocking Action: Move Down");
        event.preventDefault()
        event.stopPropagation()
        const btn = event.currentTarget
        await this.performAction(btn.dataset.url)
    }

    async toggleVisibility(event) {
        console.log("Blocking Action: Toggle Visibility");
        event.preventDefault()
        event.stopPropagation()
        const btn = event.currentTarget
        // Toggle icon state optimistic
        btn.classList.toggle('text-gray-500')
        btn.classList.toggle('text-amber-500')
        await this.performAction(btn.dataset.url)
    }

    async performAction(url) {
        console.log("Performing Action URL:", url);
        try {
            const response = await fetch(url, {
                method: "PATCH",
                headers: {
                    "Accept": "text/vnd.turbo-stream.html, text/html, application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
                }
            })

            if (response.ok) {
                const html = await response.text()
                if (html.includes('turbo-stream') && typeof Turbo !== 'undefined') {
                    Turbo.renderStreamMessage(html)
                } else {
                    // Fallback reload if not turbo stream
                    // window.location.reload()
                }
            }
        } catch (err) {
            console.error("Action failed", err)
        }
    }

    async deleteBlock(event) {
        console.log("Blocking Action: Delete");
        event.preventDefault()
        event.stopPropagation()

        const btn = event.currentTarget
        const iconContainer = btn.firstElementChild

        // Check if already confirming
        if (btn.dataset.confirming === "true") {
            // CONFIRMED DELETE
            const url = btn.dataset.url

            // Show loading state
            btn.innerHTML = `<svg class="animate-spin h-3.5 w-3.5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`

            try {
                const response = await fetch(url, {
                    method: "DELETE",
                    headers: {
                        "Accept": "text/vnd.turbo-stream.html, text/html, application/json",
                        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
                    }
                })

                if (response.ok) {
                    const html = await response.text()
                    if (html.includes('turbo-stream') && typeof Turbo !== 'undefined') {
                        Turbo.renderStreamMessage(html)
                    } else {
                        this.element.remove()
                    }
                } else {
                    console.error("Delete failed", response)
                    // Reset button
                    btn.innerHTML = `<svg class="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>`
                    btn.dataset.confirming = "false"
                    alert("Erreur lors de la suppression")
                }
            } catch (err) {
                console.error("Error deleting", err)
                btn.innerHTML = `<svg class="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>`
                btn.dataset.confirming = "false"
                alert("Erreur réseau")
            }
        } else {
            // ENTER CONFIRMATION MODE
            btn.dataset.confirming = "true"

            // Change color and icon to checkmark or red "Sure?"
            btn.classList.add('bg-red-50', 'text-red-600', 'ring-2', 'ring-red-500')
            btn.classList.remove('text-gray-400')

            // Change icon to x/check? Let's keep it trash but red and activated
            // Or change to "Sur?" text if size permits. Icon is safer for 6x6.

            // Set timeout to reset if not clicked
            setTimeout(() => {
                if (btn && btn.dataset.confirming === "true") {
                    btn.dataset.confirming = "false"
                    btn.classList.remove('bg-red-50', 'text-red-600', 'ring-2', 'ring-red-500')
                    btn.classList.add('text-gray-400')
                }
            }, 3000)
        }
    }

    // ==========================================
    // STATUS INDICATOR
    // ==========================================

    showSavingStatus() {
        const badge = this.getOrCreateStatusBadge()
        badge.innerHTML = `
            <svg class="animate-spin h-3 w-3 text-indigo-600 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-xs font-medium text-indigo-600">Saving...</span>
        `
        badge.className = "fixed bottom-5 right-5 bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-100 flex items-center z-50 transition-all transform translate-y-0 opacity-100"
    }

    showSavedStatus() {
        const badge = this.getOrCreateStatusBadge()
        badge.innerHTML = `
            <svg class="h-4 w-4 text-green-500 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span class="text-xs font-medium text-gray-700">Saved</span>
        `

        // Hide after 2 seconds
        if (this.hideTimeout) clearTimeout(this.hideTimeout)
        this.hideTimeout = setTimeout(() => {
            badge.classList.add('translate-y-10', 'opacity-0')
        }, 2000)
    }

    showErrorStatus() {
        const badge = this.getOrCreateStatusBadge()
        badge.innerHTML = `
            <svg class="h-4 w-4 text-red-500 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span class="text-xs font-medium text-red-600">Error saving</span>
        `
    }

    getOrCreateStatusBadge() {
        let badge = document.getElementById('editor-status-badge')
        if (!badge) {
            badge = document.createElement('div')
            badge.id = 'editor-status-badge'
            badge.className = "fixed bottom-5 right-5 bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-100 flex items-center z-50 transition-all transform translate-y-10 opacity-0"
            document.body.appendChild(badge)
        }
        return badge
    }
}
